import React, { useEffect, useMemo, useState } from "react";
import MainPageDialog from "../dialog";
import {
  hasMissingAttachmentId,
  useFileUploadMutation,
  useMarkDocumentStatusMutation,
  useSaveSignedDocumentMutation,
  useUpdateAttachmentMutation,
  useUploadAttachmentMutation,
} from "../../services/chatService";
import { toast } from "react-toastify";
import PDFViewer from "../Foxit7.5";
import { useGroupChatContext } from "../../context/groupchatContext";
import { cloneDeep } from "lodash";
import { CheckBox } from "../reactHookFormFields/checkbox";
import { useForm, useWatch } from "react-hook-form";
import FullScreenLoader from "../FullScreenLoader";
import baseApiHelper from "../../services/baseApiHelper";


// Global variable to track if Foxit resources are loaded
window.isFoxitLoaded = window.isFoxitLoaded || false;

function FoxitDialog({
  foxitDialogState,
  setFoxitDialogState,
  file,
  callBackFun,
  isFromLettersOrders,
  setAttachedFiles,
  showMarkReview = false,
  messageId,
  pid,
  location_id,
  chat_id,
  onCloseAttachment,
  readOnly = false,
  includeOrigAttachId = false,
  enableSignDocument = false,
}) {
  const { control, getValues, setValue } = useForm();
  const [uploadAttachment, { isLoading: isUploading }] =
    useUploadAttachmentMutation();
  const [fileUpload] = useFileUploadMutation();
  const [updateAttachment] = useUpdateAttachmentMutation();
  const [pdfUiIstance, setPdfUiIstance] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isSigningDocument, setIsSigningDocument] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState({
    cssLoaded: false,
    scriptLoaded: false,
  });
  const [markDocumentStatus, { isLoading: isMarking }] = useMarkDocumentStatusMutation();
  const [saveSignedDocument, { isLoading: isSavingSignedDocument }] = useSaveSignedDocumentMutation();
  const [loadingStatus, setLoadingStatus] = useState(null);
  const groupChatContext = useGroupChatContext();

  const { authData, selectedChatTab, selectedChat } = useMemo(() => ({
    authData: groupChatContext.authData,
    selectedChatTab: groupChatContext.selectedChatTab,
    selectedChat: groupChatContext.selectedChat,
  }));

  const handleClose = () => {
    setFoxitDialogState(false);
  
    onCloseAttachment?.();
  };
  const linkToPatientChart = useWatch({
    control,
    name: "is_link_to_patient_chart",
  });
  useEffect(() => {
    if (file) {
      setValue("is_link_to_patient_chart", file?.is_link_to_patient_chart);
    }
  }, [file]);
  const exportDocToUrl = (type) => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      if (!pdfUiIstance) {
        return toast.error("pdfui instance not found");
      }
      pdfUiIstance
        .getPDFViewer()
        .then((viewer) => {
          const bufferArray = [];
          viewer
            .getCurrentPDFDoc()
            .getStream((streamData) => {
              bufferArray.push(streamData.arrayBuffer);
            })
            .then((size) => {
              const blob = new Blob(bufferArray, { type: "application/pdf" });
              const fd = new FormData();
              fd.append("file", blob);
              if (isFromLettersOrders) {
                uploadAttachment(fd).then((res) => {
                  if (res?.data?.response?.code == 0) {
                    const attachmentId = res?.data?.response?.data?.attachment_id;
                    if (!String(attachmentId || "").trim()) {
                      setIsLoading(false);
                      toast.error("Attachment failed to upload. Please try again.");
                      return;
                    }
                    if (type == "save_and_share") {
                      setAttachedFiles((prevAttachment) => {
                        const newArray = [
                          ...prevAttachment,
                          {
                            attachment_id: attachmentId,
                            mime_type: "application/pdf",
                            title: file?.title,
                          },
                        ];
                        return newArray;
                      });
                      callBackFun("close_letters_orders");
                    }                    
                    updateAttachment({
                      entity_id: file?.entity_id,
                      entity: file?.entity,
                      new_doc_uuid: attachmentId,
                    }).then((res) => {
                      setIsLoading(false);
                      setFoxitDialogState(false);
                      baseApiHelper().invalidateTags(["LettersList"]);
                    });
                  }
                });
              } else {
                fileUpload(fd).then(async (res) => {
                  if (typeof callBackFun == "function") {
                    let response = cloneDeep(res?.data?.data);
                    if (hasMissingAttachmentId([response])) {
                      setIsLoading(false);
                      toast.error("Attachment failed to upload. Please try again.");
                      return;
                    }
                    response.title = file?.title ? file.title : response.title;
                    if (type === "save_and_send") {
                      response.status = "signed";
                    }
                    if (linkToPatientChart) {
                      response.existing_attachment = file?.attachment_id;
                    }
                    response.is_link_to_patient_chart = linkToPatientChart;
                    await callBackFun([response], {
                      pid,
                      location_id,
                      ...(type === "save_and_send" ? { sign_status: true } : {}),
                      ...(type === "save_and_send" && includeOrigAttachId && file?.attachment_id ? { orig_attach_id: file.attachment_id } : {}),
                      ...(showMarkReview ? { message_id: file?.message_id } : {}),
                      ...(showMarkReview ? { original_doc_id: messageId } : {}),
                    });
                  }

                  setIsLoading(false);
                  handleClose();
                });
              }
            })
            .catch(reject);
        })
        .catch(reject);
    });
  };
  const signDocument = () => {
    setIsSigningDocument(true);
    return new Promise((resolve, reject) => {
      if (!pdfUiIstance) {
        setIsSigningDocument(false);
        toast.error("pdfui instance not found");
        return reject();
      }

      pdfUiIstance
        .getPDFViewer()
        .then((viewer) => {
          const bufferArray = [];
          viewer
            .getCurrentPDFDoc()
            .getStream((streamData) => {
              bufferArray.push(streamData.arrayBuffer);
            })
            .then(async () => {
              try {
                const blob = new Blob(bufferArray, { type: "application/pdf" });
                const fd = new FormData();
                fd.append("file", blob);

                const uploadRes = await fileUpload(fd).unwrap();
                const uploadedFile = cloneDeep(uploadRes?.data);
                const attachmentUuid = uploadedFile?.attachment_uuid || uploadedFile?.attachment_id;

                if (!String(attachmentUuid || "").trim()) {
                  toast.error("Attachment failed to upload. Please try again.");
                  return;
                }

                const res = await saveSignedDocument({
                  attachment_uuid: attachmentUuid,
                  path: uploadedFile?.path || uploadedFile?.attachment_path,
                  title: file?.title,
                  mime_type: uploadedFile?.mime_type || "application/pdf",
                  size: uploadedFile?.file_size || uploadedFile?.size,
                  location_id,
                  pid,
                  sign_status: true,
                  signed_document_uuid: file?.attachment_uuid || file?.attachment_id,
                  chat_id: chat_id || selectedChat?.chat_id || selectedChat?.id,
                  message_id: file?.message_id || messageId
                }).unwrap();

                if (res?.code === 0) {
                  toast.success(res?.msg || "Document signed successfully.");
                  handleClose();
                } else {
                  toast.error(res?.msg || "Failed to sign document.");
                }
              } catch (error) {
                toast.error("Failed to sign document.");
              } finally {
                setIsSigningDocument(false);
              }
            })
            .catch((error) => {
              setIsSigningDocument(false);
              toast.error("Failed to sign document.");
              reject(error);
            });
        })
        .catch((error) => {
          setIsSigningDocument(false);
          toast.error("Failed to sign document.");
          reject(error);
        });
    });
  };
  const markDocumentStatusHandler = async (status) => {
    try {
      setLoadingStatus(status);
  
      const payload = {
        attachment_id: messageId,
        status: status,
      };

      if (linkToPatientChart) {
        payload.is_link_to_patient_chart = true;
      }

      const res = await markDocumentStatus(payload).unwrap();
  
      const message = res?.response?.msg;
  
      if (message) {
        toast.success(message);
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoadingStatus(null);
    }
  };
  const footerlist = [
    {
      title: "Close",
      action: handleClose,
      type: "cancel",
    },
    ...(showMarkReview
      ? [
          {
            title: loadingStatus === "reviewed" ? "Saving..." : "Mark Review",
            action: () => markDocumentStatusHandler("reviewed"),
            type: "save",
            loading: loadingStatus === "reviewed",
          },
          {
            title: loadingStatus === "followed up" ? "Saving..." : "Follow-Up Request",
            action: () => markDocumentStatusHandler("followed up"),
            type: "save",
            loading: loadingStatus === "followed up",
          },
        ]
      : []),  
    ...(authData?.is_provider && !isFromLettersOrders && !readOnly
      ? [
          {
            title: "Sign and Send",
            action: () => exportDocToUrl("save_and_send"),
            type: "save",
            loading: isLoading,
          },
          enableSignDocument && {
            title: "Sign Document",
            action: signDocument,
            type: "save",
            loading: isSavingSignedDocument || isSigningDocument,
          },
          (selectedChatTab === "patient_chats" &&
          !file?.is_link_to_patient_chart) && {
            type: "content",
            content: (
              <CheckBox
                control={control}
                name="is_link_to_patient_chart"
                title="Link to Patient Chart"
              />
            ),
          },
        ]
      : isFromLettersOrders
      ? [
          {
            title: "Save",
            action: () => exportDocToUrl(),
            type: "save",
            loading: isLoading,
          },
          {
            title: "Save and Share",
            action: () => exportDocToUrl("save_and_share"),
            type: "save",
            loading: isLoading,
          },
        ]
      : []),
  ];
  const userData = JSON.parse(localStorage.getItem("userData"));

  useEffect(() => {
    if (window.isFoxitLoaded) {
      setIsScriptLoaded(window.isFoxitLoaded);
    }
  }, [window.isFoxitLoaded]);

  useEffect(() => {
    const handleFoxitStatusChange = () => {
      console.log("Foxit status changed event received:", window.isFoxitLoaded);
      setIsScriptLoaded(window.isFoxitLoaded);
    };

    // Set initial state
    setIsScriptLoaded(window.isFoxitLoaded);

    // Add event listener
    window.addEventListener("foxitStatusChanged", handleFoxitStatusChange);

    // Cleanup
    return () => {
      window.removeEventListener("foxitStatusChanged", handleFoxitStatusChange);
    };
  }, []);

  if (!isScriptLoaded?.cssLoaded || !isScriptLoaded?.scriptLoaded) {
    return <FullScreenLoader />;
  }

  return (
    <>
      {foxitDialogState ? (
        <MainPageDialog
          title={file?.title ? file.title : "Pdf Veiwer"}
          footerList={footerlist}
          maxHeight={true}
          onCancelHandler={handleClose}
        >
          <div className="h-[80vh]">
            <PDFViewer
              fileUrl={file?.attachment_id}
              setPdfUiIstance={setPdfUiIstance}
            />
            {/* <Foxit fileUrl={uuid} setPdfUiIstance={setPdfUiIstance} /> */}
          </div>
        </MainPageDialog>
      ) : null}
    </>
  );
}

export default FoxitDialog;
